import React from 'react';
import useThemeColor from '../../../hooks/useThemeColor';
import InlinePageSizeSelector from '../InlinePageSizeSelector/InlinePageSizeSelector';

const Pagination = ({ totalRecords, pageSize, pageNumber, totalPages, setPageNumber, setPageSize }) => {
    const themeColor = useThemeColor();
    const toNumber = (value, fallback = 0) => {
        if (value !== null && typeof value === 'object' && 'value' in value) {
            const parsedFromOption = Number(value.value);
            return Number.isFinite(parsedFromOption) ? parsedFromOption : fallback;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    };

    const safeTotalRecords = Math.max(0, toNumber(totalRecords, 0));
    const safePageSize = Math.max(1, toNumber(pageSize, 50));
    const safePageNumber = Math.max(1, toNumber(pageNumber, 1));
    const safeTotalPages = Math.max(1, toNumber(totalPages, 1));
    const startRecord = safeTotalRecords === 0 ? 0 : ((safePageNumber - 1) * safePageSize) + 1;
    const endRecord = safeTotalRecords === 0 ? 0 : Math.min(safeTotalRecords, safePageNumber * safePageSize);

    const getPageNumbers = () => {
        const maxVisible = 7; // max number of page buttons (excluding Prev/Next)
        if (safeTotalPages <= maxVisible) {
            return Array.from({ length: safeTotalPages }, (_, i) => i + 1);
        }

        const pages = [];
        const windowSize = 3; // how many pages to show around current
        let start = Math.max(2, safePageNumber - windowSize);
        let end = Math.min(safeTotalPages - 1, safePageNumber + windowSize);

        // Ensure we always show a consistent window size when possible
        const visibleCount = end - start + 1;
        if (visibleCount < windowSize * 2 + 1) {
            if (start === 2) {
                end = Math.min(safeTotalPages - 1, start + windowSize * 2);
            } else if (end === safeTotalPages - 1) {
                start = Math.max(2, end - windowSize * 2);
            }
        }

        pages.push(1); // always show first page

        if (start > 2) {
            pages.push('left-ellipsis');
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (end < safeTotalPages - 1) {
            pages.push('right-ellipsis');
        }

        if (safeTotalPages > 1) {
            pages.push(safeTotalPages); // always show last page
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <div className="d-md-flex mt-3 align-items-center">
            <div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
                <div className="d-flex align-items-center gap-3">
                    <InlinePageSizeSelector
                        defaultValue={pageSize}
                        onChange={setPageSize}  
                        className="bottom-controls"
                    />
                    <span className="stats-text d-flex align-items-center" style={{ height: '32px' }}>
                        Showing {startRecord} to {endRecord} of {safeTotalRecords} records.
                    </span>
                </div>
            </div>
            <ul className="pagination mb-0 justify-content-center">
                <li className={`page-item ${safePageNumber === 1 ? "disabled" : ""}`}>
                    <button
                        className="page-link"
                        onClick={() => setPageNumber(safePageNumber - 1)}
                        style={{
                            backgroundColor: safePageNumber === 1 ? '#f8f9fa' : 'transparent',
                            color: safePageNumber === 1 ? '#6c757d' : themeColor,
                            borderColor: themeColor
                        }}
                    >
                        Previous
                    </button>
                </li>
                {pageNumbers.map((item, index) => {
                    if (item === 'left-ellipsis' || item === 'right-ellipsis') {
                        return (
                            <li key={item + index} className="page-item disabled">
                                <span
                                    className="page-link"
                                    style={{
                                        backgroundColor: 'transparent',
                                        color: themeColor,
                                        borderColor: themeColor,
                                        cursor: 'default'
                                    }}
                                >
                                    ...
                                </span>
                            </li>
                        );
                    }

                    const page = item;
                    return (
                        <li key={page} className={`page-item ${page === safePageNumber ? "active" : ""}`}>
                            <button
                                className="page-link"
                                onClick={() => setPageNumber(page)}
                                style={{
                                    backgroundColor: page === safePageNumber ? themeColor : 'transparent',
                                    color: page === safePageNumber ? 'white' : themeColor,
                                    borderColor: themeColor
                                }}
                            >
                                {page}
                            </button>
                        </li>
                    );
                })}
                <li className={`page-item ${safePageNumber === safeTotalPages ? "disabled" : ""}`}>
                    <button
                        className="page-link"
                        onClick={() => setPageNumber(safePageNumber + 1)}
                        style={{
                            backgroundColor: safePageNumber === safeTotalPages ? '#f8f9fa' : 'transparent',
                            color: safePageNumber === safeTotalPages ? '#6c757d' : themeColor,
                            borderColor: themeColor
                        }}
                    >
                        Next
                    </button>
                </li>
            </ul>
        </div>
    );
};

export default Pagination;